// src/constants/challenges.ts

export type ChallengeMetric =
  | 'uniqueSpeciesCount'
  | 'uniqueWatersCount'
  | 'sharedContributions'
  | 'uniqueTechniquesCount'
  | 'uniqueConditionCount'
  | 'uniqueWaterTypeCount';

export interface ChallengeWithXP {
  id: string;
  key: string;
  title: string;
  description: string;
  rewardXP: number;
  points: number;
  goal: number;
  metric: ChallengeMetric;
  type: 'all_time';
  category: 'exploration' | 'contribution' | 'mastery';
  legacyId?: string;
}

export interface AchievementCatalogItem {
  key: string;
  title: string;
  description: string;
  rewardXP: number;
  category: 'exploration' | 'contribution' | 'mastery' | 'consistency';
}

/**
 * Launch-safe challenge catalog.
 * Keep these focused on variety, exploration, useful details, and contribution.
 * Avoid raw catch-count ladders here; same-day catch quantity should be balanced
 * in the backend catch XP award with diminishing returns.
 */
export const challenges: ChallengeWithXP[] = [
  {
    id: 'species_3_all_time',
    key: 'species_3_all_time',
    legacyId: 'challenge_1',
    title: 'Species Sampler',
    description: 'Log 3 unique species.',
    rewardXP: 24,
    points: 24,
    goal: 3,
    metric: 'uniqueSpeciesCount',
    type: 'all_time',
    category: 'exploration',
  },
  {
    id: 'species_10_all_time',
    key: 'species_10_all_time',
    title: 'Local Range Finder',
    description: 'Log 10 unique species across your fishing history.',
    rewardXP: 60,
    points: 60,
    goal: 10,
    metric: 'uniqueSpeciesCount',
    type: 'all_time',
    category: 'mastery',
  },
  {
    id: 'species_25_all_time',
    key: 'species_25_all_time',
    title: 'Species Explorer',
    description: 'Catch 25 unique species.',
    rewardXP: 120,
    points: 120,
    goal: 25,
    metric: 'uniqueSpeciesCount',
    type: 'all_time',
    category: 'mastery',
  },
  {
    id: 'waters_2_all_time',
    key: 'waters_2_all_time',
    title: 'Water Wanderer',
    description: 'Log catches on 2 unique waters.',
    rewardXP: 20,
    points: 20,
    goal: 2,
    metric: 'uniqueWatersCount',
    type: 'all_time',
    category: 'exploration',
  },
  {
    id: 'waters_10_all_time',
    key: 'waters_10_all_time',
    title: 'Blue Map Scout',
    description: 'Log catches on 10 unique waters.',
    rewardXP: 80,
    points: 80,
    goal: 10,
    metric: 'uniqueWatersCount',
    type: 'all_time',
    category: 'exploration',
  },
  {
    id: 'techniques_3_all_time',
    key: 'techniques_3_all_time',
    title: 'Technique Mixer',
    description: 'Log catches using 3 unique techniques.',
    rewardXP: 24,
    points: 24,
    goal: 3,
    metric: 'uniqueTechniquesCount',
    type: 'all_time',
    category: 'mastery',
  },
  {
    id: 'techniques_8_all_time',
    key: 'techniques_8_all_time',
    title: 'Pattern Builder',
    description: 'Log catches using 8 unique techniques.',
    rewardXP: 70,
    points: 70,
    goal: 8,
    metric: 'uniqueTechniquesCount',
    type: 'all_time',
    category: 'mastery',
  },
  {
    id: 'shared_5_all_time',
    key: 'shared_5_all_time',
    title: 'Community Contributor',
    description: 'Share 5 useful FishIQ contributions.',
    rewardXP: 32,
    points: 32,
    goal: 5,
    metric: 'sharedContributions',
    type: 'all_time',
    category: 'contribution',
  },
  {
    id: 'shared_25_all_time',
    key: 'shared_25_all_time',
    title: 'Signal Builder',
    description: 'Share 25 useful FishIQ contributions.',
    rewardXP: 90,
    points: 90,
    goal: 25,
    metric: 'sharedContributions',
    type: 'all_time',
    category: 'contribution',
  },
  {
    id: 'conditions_3_all_time',
    key: 'conditions_3_all_time',
    title: 'Condition Reader',
    description: 'Log catches across 3 distinct time, weather, or water signals.',
    rewardXP: 24,
    points: 24,
    goal: 3,
    metric: 'uniqueConditionCount',
    type: 'all_time',
    category: 'mastery',
  },
  {
    id: 'habitats_3_all_time',
    key: 'habitats_3_all_time',
    title: 'Habitat Mixer',
    description: 'Log catches across 3 distinct water or habitat types.',
    rewardXP: 24,
    points: 24,
    goal: 3,
    metric: 'uniqueWaterTypeCount',
    type: 'all_time',
    category: 'exploration',
  },
];

export const achievementCatalog: AchievementCatalogItem[] = [
  {
    key: 'first_catch',
    title: 'First Catch',
    description: 'Logged your first catch.',
    rewardXP: 12,
    category: 'consistency',
  },
  {
    key: 'first_species',
    title: 'New Species',
    description: 'Logged your first catch of a new species.',
    rewardXP: 14,
    category: 'exploration',
  },
  {
    key: 'first_water',
    title: 'New Water',
    description: 'Logged your first catch on a new water.',
    rewardXP: 10,
    category: 'exploration',
  },
  {
    key: 'first_region',
    title: 'Regional Explorer',
    description: 'Logged your first catch in a new region.',
    rewardXP: 14,
    category: 'exploration',
  },
  {
    key: 'first_technique',
    title: 'Technique On The Board',
    description: 'Logged your first distinct technique.',
    rewardXP: 8,
    category: 'exploration',
  },
  {
    key: 'first_shared_contribution',
    title: 'First Contribution',
    description: 'Shared your first FishIQ contribution.',
    rewardXP: 16,
    category: 'contribution',
  },
  {
    key: 'new_water_scout',
    title: 'New Water Scout',
    description: 'Explored a new named water.',
    rewardXP: 12,
    category: 'exploration',
  },
  {
    key: 'consistent_closer',
    title: 'Consistent Closer',
    description: 'Logged a catch on 3 different days.',
    rewardXP: 18,
    category: 'consistency',
  },
  {
    key: 'condition_reader',
    title: 'Condition Reader',
    description: 'Built a pattern across multiple condition types.',
    rewardXP: 18,
    category: 'mastery',
  },
  {
    key: 'habitat_variety',
    title: 'Habitat Mixer',
    description: 'Logged catches across multiple water or habitat types.',
    rewardXP: 18,
    category: 'exploration',
  },
];

export const challengeById = Object.fromEntries(challenges.map((c) => [c.id, c])) as Record<
  string,
  ChallengeWithXP
>;

export const challengeByLegacyId = Object.fromEntries(
  challenges
    .filter((c) => c.legacyId)
    .map((c) => [String(c.legacyId), c]),
) as Record<string, ChallengeWithXP>;

export const achievementByKey = Object.fromEntries(
  achievementCatalog.map((a) => [a.key, a]),
) as Record<string, AchievementCatalogItem>;
